/**
 * editorSharedStyles — single source of truth for shared inline-style
 * presets used across ALL Synapse editors (Flow, Console, future editors).
 *
 * Import from here. Any change here propagates to every editor automatically.
 *
 * Editor-specific styles (Flow: brush cards, BPM popup, highway backdrop;
 * Console: palette buttons; Synth VR: highway CSS classes) stay in each
 * editor's own styles file.
 *
 * All colors use oklch() with CSS custom properties from tokens.css so the
 * whole app re-tints when the accent / tint theme changes.
 */

import React from 'react';
import { color, font } from './theme';

// ── Shell ─────────────────────────────────────────────────────────────────────

export const shellStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'grid',
  gridTemplateRows: 'minmax(0, 1fr)',
  // Outer margins + inner gap, all tunable via shared CSS vars (Layout tuner):
  //   --shell-gap        gap between a sidebar's inner edge and the canvas
  //   --shell-pad-left   left sidebar's distance from the screen edge
  //   --shell-pad-y      sidebars' top + bottom margin
  // Each falls back to the global --sidebar-x so untouched editors keep the
  // uniform 16px margins.
  gap: 'var(--shell-gap, 16px)',
  paddingTop: 'var(--shell-pad-y, 16px)',
  paddingRight: 'var(--sidebar-x, 16px)',
  paddingBottom: 'var(--shell-pad-y, 16px)',
  paddingLeft: 'var(--shell-pad-left, var(--sidebar-x, 16px))',
  background: 'radial-gradient(ellipse at top, var(--bg-glow) 0%, var(--bg-0) 65%)',
  color: color.ink,
  fontFamily: font.sans,
};

export const leftPanelStyle: React.CSSProperties = {
  background: 'transparent',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  overflowY: 'auto',
  minHeight: 0,
};

// The inner scroll column shared by all editors: fills the remaining height
// under the fixed header and scrolls vertically. The edge fade is applied
// dynamically by <ScrollFade> (only the side with hidden content fades). The
// header (editor pill / song card) lives OUTSIDE this element so the scrollbar
// range and the fade leave it alone.
export const sidebarScrollStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  overflowY: 'auto',
};

export const topScrollStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  overflowY: 'auto',
  // Reserve the scrollbar gutter on both edges so content stays centred rather
  // than shoved left when the bar appears.
  scrollbarGutter: 'stable both-edges',
  marginBottom: 10,
  minHeight: 0,
};

export const rightPanelStyle: React.CSSProperties = {
  background: 'transparent',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  overflowY: 'auto',
  minHeight: 0,
};

export const centerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',
};

// ── Group cards ───────────────────────────────────────────────────────────────

export const groupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '7px 9px',
  background: 'oklch(0.065 calc(0.016 * var(--tint-c)) var(--tint-h) / 0.62)',
  // Subtle disguised outline (same as the inner containers), matching the Flow
  // editor — not the old bright accent border.
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  borderRadius: 16,
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
};

export const groupLabelStyle: React.CSSProperties = {
  fontSize: 11,
  letterSpacing: 1.4,
  color: color.accentSoft,
  textTransform: 'uppercase',
  fontWeight: 700,
  textAlign: 'center',
  marginBottom: 1,
};

// ── Button states ─────────────────────────────────────────────────────────────

/**
 * Canonical active/inactive state for every toggle or selectable button.
 * active   → accent gradient fill + accent border + accent glow
 * inactive → tint surface + faint tint outline + faint text
 */
export function editorBtnState(active: boolean): React.CSSProperties {
  return active
    ? {
        background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))',
        border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
        color: color.accent,
        boxShadow: '0 0 10px oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15)',
      }
    : {
        background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
        border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
        color: color.inkFaint,
        boxShadow: 'none',
      };
}

// ── Stepper controls ──────────────────────────────────────────────────────────

// Layout: [ LABEL ]  ( ◄  value  ► ) — label sits OUTSIDE, to the left; the
// arrows + value live together in a rounded pill (arrows circular, value
// centred). The row itself is transparent (no border/box).
export const stepperStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
};

// Fixed-width label outside the pill so every row's pill starts at the same x.
export const stepperLabelStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 46,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.5,
  color: color.inkFaint,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// The pill: arrows pinned to the ends, value centred between them.
export const stepperPillStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 6,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  borderRadius: 999,
  // Tighter horizontal padding so the ◀ ▶ buttons hug the pill's rounded ends.
  padding: '3px 3px',
  boxSizing: 'border-box',
};

export const stepperBtnStyle: React.CSSProperties = {
  flexShrink: 0,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  // Accent outline at rest; hover fills the button with accent (arrow → tint).
  border: '1px solid var(--accent)',
  color: color.accent,
  borderRadius: '50%',
  transition: 'background 0.12s ease, color 0.12s ease',
  width: 24,
  height: 24,
  cursor: 'pointer',
  fontSize: 9,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const stepperValueStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
  fontSize: 13,
  fontWeight: 600,
  color: color.ink,
  fontVariantNumeric: 'tabular-nums',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const stepperInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  textAlign: 'center',
  fontSize: 13,
  fontWeight: 600,
  color: color.ink,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  padding: 0,
  fontFamily: font.sans,
  fontVariantNumeric: 'tabular-nums',
  boxSizing: 'border-box',
};

// ── Auto-detect row ───────────────────────────────────────────────────────────

export const autoRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 6,
};

export const autoBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  height: 26,
  border: '1px solid',
  borderRadius: 50,
  // Smaller text + no-wrap so "AUTO OFFSET" fits on a single line in its column.
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
  fontFamily: font.sans,
  transition: 'all 0.15s ease',
  cursor: 'pointer',
  padding: '0 6px',
  boxSizing: 'border-box',
};

// ── Camera view ───────────────────────────────────────────────────────────────

export const cameraGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 4,
};

export const cameraBtnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  padding: '5px 4px 4px',
  borderRadius: 12,
  border: '1px solid',
  cursor: 'pointer',
  fontFamily: font.sans,
  letterSpacing: 0.4,
  transition: 'all 0.15s ease',
};

export const cameraActionBtnStyle: React.CSSProperties = {
  width: '100%',
  marginTop: 6,
  padding: '6px 8px',
  borderRadius: 50,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  color: color.inkDim,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.5,
  cursor: 'pointer',
  fontFamily: font.sans,
};

// ── Bottom action section ─────────────────────────────────────────────────────

export const bottomSectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: 10,
  background: 'oklch(0.065 calc(0.016 * var(--tint-c)) var(--tint-h) / 0.62)',
  border: '1.5px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.35)',
  borderRadius: 16,
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
};

export const bottomBtnsStyle: React.CSSProperties = {
  // Two equal columns. `minmax(0, 1fr)` (rather than flex:1) guarantees both
  // buttons are exactly the same width even when their icon+label content
  // differs in width — keeps paired rows like New/Open and Save/Clear symmetric.
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: 6,
};

export const uniformBtn: React.CSSProperties = {
  flex: 1,
  borderRadius: 50,
  padding: '7px 12px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: font.sans,
  letterSpacing: 0.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  textAlign: 'center',
  transition: 'all 0.2s ease',
};

// Matches the inactive state of every other editor button (editorBtnState(false))
// so the Map / action buttons read the same as the Camera View / control grids
// instead of standing out.
export const mapBtnVariant: React.CSSProperties = {
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
  color: color.inkFaint,
};

export const clearBtnVariant: React.CSSProperties = {
  background: 'rgba(239, 68, 68, 0.08)',
  border: '1px solid rgba(239, 68, 68, 0.2)',
  color: '#F87171',
};

// ── Toast notifications ───────────────────────────────────────────────────────

/**
 * Base toast style — each editor spreads this and adds its own left/bottom
 * offset (which varies with the sidebar width).
 */
export const toastBaseStyle: React.CSSProperties = {
  position: 'fixed',
  padding: '8px 14px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.6,
  fontFamily: font.sans,
  pointerEvents: 'none',
  zIndex: 4000,
  whiteSpace: 'nowrap',
  boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.92)',
  border: '1px solid rgba(34, 197, 94, 0.4)',
  color: '#A7F3D0',
};

export const toastErrorBaseStyle: React.CSSProperties = {
  ...toastBaseStyle,
  border: '1px solid rgba(239, 68, 68, 0.4)',
  color: '#FCA5A5',
};

// ── Drop overlay ──────────────────────────────────────────────────────────────

export const dropOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 60,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  background: 'rgba(10, 6, 25, 0.6)',
  border: '2px dashed oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.8)',
  borderRadius: 12,
  backdropFilter: 'blur(2px)',
};

export const dropCardStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '22px 30px',
  borderRadius: 14,
  background: 'oklch(0.20 calc(0.030 * var(--tint-c)) var(--tint-h) / 0.92)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  color: '#F5E8FF',
  fontFamily: font.sans,
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: 0.3,
  boxShadow: '0 14px 40px rgba(0,0,0,0.55)',
};

// ── Transport bar ─────────────────────────────────────────────────────────────

export const transportStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 10px',
  background: 'oklch(0.065 calc(0.016 * var(--tint-c)) var(--tint-h) / 0.62)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  borderRadius: 12,
  fontSize: 11,
  flexShrink: 0,
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
};

// ── Segmented pill (React-to / Camera / Mirror / Others) ───────────────────────
export const reactSegStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: 3,
  borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
};
export const reactSegLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.4,
  color: color.inkFaint,
  padding: '0 6px',
  whiteSpace: 'nowrap',
};
export const reactSegBtnStyle: React.CSSProperties = {
  flex: 1,
  height: 24,
  borderRadius: 999,
  border: '1px solid transparent',
  background: 'transparent',
  color: color.inkFaint,
  fontFamily: font.sans,
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
};
export const reactSegBtnActiveStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h)/0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h)/0.15))',
  borderColor: 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h)/0.5)',
  color: 'var(--accent)',
};

export const transportBtn: React.CSSProperties = {
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid var(--accent)',
  color: 'var(--accent)',
  borderRadius: 50,
  padding: '5px 12px',
  cursor: 'pointer',
  fontSize: 13,
  fontFamily: font.sans,
};

export const timeReadout: React.CSSProperties = {
  color: color.ink,
  fontFamily: font.sans,
  fontSize: 11,
  fontVariantNumeric: 'tabular-nums',
};

export const beatReadout: React.CSSProperties = {
  color: 'var(--accent)',
  fontSize: 11,
  fontVariantNumeric: 'tabular-nums',
};

// ── Shared modal shell ────────────────────────────────────────────────────────

export const popupOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'oklch(0 0 0 / 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
};

export const popupWindowStyle: React.CSSProperties = {
  background: 'var(--surface-panel)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.25)',
  borderRadius: 18,
  boxShadow: '0 12px 48px rgba(0,0,0,0.6), 0 0 30px oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.10)',
  overflow: 'hidden',
  userSelect: 'none',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  minWidth: 360,
  maxWidth: 420,
  display: 'flex',
  flexDirection: 'column',
};

export const popupTitleBarStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 16px',
  borderBottom: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.12)',
  flexShrink: 0,
  background: 'oklch(0.065 calc(0.016 * var(--tint-c)) var(--tint-h) / 0.8)',
};

/** The minimap's container box — identical across editors (the 3D scene inside
 *  is editor-specific). 220px tall, hairline border, near-black inset. */
export const minimapWrapStyle: React.CSSProperties = {
  width: '100%',
  height: 220,
  borderRadius: 8,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.2)',
  overflow: 'hidden',
  cursor: 'pointer',
  background: 'oklch(0.10 calc(0.02 * var(--tint-c)) var(--tint-h))',
};
